<script setup>
import { computed, onMounted, ref } from 'vue'
import { api, asList } from '../api/client'
import { indexIcalBlocksByDay } from '../api/ical'
import { normalizeBooking, normalizeResource, resourceTypeLabels, serializeBooking } from '../api/mappers'
import { addDays, bookingOverlapsDay, formatDate, formatTime, sameDay, startOfWeek, toDateInput } from '../utils'
import AppIcon from '../components/AppIcon.vue'
import AvailabilityPanel from '../components/AvailabilityPanel.vue'
import BookingDialog from '../components/BookingDialog.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import { useToast } from '../composables/useToast'
import { useSession } from '../composables/useSession'
import { canManageCatalog } from '../api/users'

const { show } = useToast()
const session = useSession()
const canManage = computed(() => canManageCatalog(session.user))
const viewMode = ref('week')
const focusedDate = ref(startOfWeek())
const resources = ref([])
const bookings = ref([])
const calendarBlocks = ref([])
const loading = ref(true)
const saving = ref(false)
const typeFilter = ref('')
const dialogOpen = ref(false)
const editingBooking = ref(null)
const initialResourceId = ref(null)
const initialStartsAt = ref('')
const initialEndsAt = ref('')
const availabilityPanel = ref(null)
const loadError = ref('')
let loadSequence = 0

const visibleStart = computed(() => (
  viewMode.value === 'week' ? startOfWeek(focusedDate.value) : startOfMonth(focusedDate.value)
))
const days = computed(() => (
  viewMode.value === 'week' ? weekDays(visibleStart.value) : monthDays(visibleStart.value)
))

function startOfMonth(value = new Date()) {
  const calendarDate = toDateInput(value)
  return new Date(`${calendarDate.slice(0, 7)}-01T12:00:00`)
}

function weekDays(value) {
  return Array.from({ length: 7 }, (_, index) => addDays(value, index))
}

function monthDays(value) {
  const year = value.getFullYear()
  const month = value.getMonth()
  const count = new Date(year, month + 1, 0).getDate()
  return Array.from({ length: count }, (_, index) => new Date(year, month, index + 1, 12))
}

function addMonths(value, amount) {
  const next = new Date(value)
  next.setDate(1)
  next.setMonth(next.getMonth() + amount)
  return next
}

function monthGenitive(date) {
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
    .formatToParts(date)
    .find((part) => part.type === 'month')?.value || ''
}

const weekLabel = computed(() => {
  const end = addDays(visibleStart.value, 6)
  const startMonth = monthGenitive(visibleStart.value)
  const endMonth = monthGenitive(end)
  const sameYear = visibleStart.value.getFullYear() === end.getFullYear()
  if (sameYear && visibleStart.value.getMonth() === end.getMonth()) {
    return `${visibleStart.value.getDate()}–${end.getDate()} ${endMonth} ${end.getFullYear()}`
  }
  if (sameYear) {
    return `${visibleStart.value.getDate()} ${startMonth} — ${end.getDate()} ${endMonth} ${end.getFullYear()}`
  }
  return `${visibleStart.value.getDate()} ${startMonth} ${visibleStart.value.getFullYear()} — ${end.getDate()} ${endMonth} ${end.getFullYear()}`
})
const monthLabel = computed(() => new Intl.DateTimeFormat('ru-RU', {
  month: 'long',
  year: 'numeric',
}).format(visibleStart.value))
const periodLabel = computed(() => (viewMode.value === 'week' ? weekLabel.value : monthLabel.value))
const previousPeriodLabel = computed(() => (
  viewMode.value === 'week' ? 'Предыдущая неделя' : 'Предыдущий месяц'
))
const nextPeriodLabel = computed(() => (
  viewMode.value === 'week' ? 'Следующая неделя' : 'Следующий месяц'
))
const filteredResources = computed(() => resources.value.filter((resource) => (
  resource.isActive && (!typeFilter.value || resource.resourceType === typeFilter.value)
)))
const activeResources = computed(() => resources.value.filter((resource) => resource.isActive))

const bookingsByCell = computed(() => {
  const cells = new Map()
  bookings.value.forEach((booking) => {
    if (booking.status === 'cancelled') return
    days.value.forEach((day) => {
      if (!bookingOverlapsDay(booking, day)) return
      const key = `${booking.resourceId}-${toDateInput(day)}`
      const existing = cells.get(key) || []
      existing.push(booking)
      cells.set(key, existing)
    })
  })
  return cells
})

const blocksByCell = computed(() => indexIcalBlocksByDay(
  calendarBlocks.value,
  days.value.map(toDateInput),
))

function bookingsFor(resourceId, day) {
  return bookingsByCell.value.get(`${resourceId}-${toDateInput(day)}`) || []
}

function blocksFor(resourceId, day) {
  return blocksByCell.value.get(`${resourceId}-${toDateInput(day)}`) || []
}

function blockDateLabel(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return '—'
  const [year, month, day] = value.split('-').map(Number)
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })
    .format(new Date(year, month - 1, day, 12))
}

function enrichBooking(raw) {
  const booking = normalizeBooking(raw)
  if (!booking.resource?.id) {
    const resource = resources.value.find((item) => String(item.id) === String(booking.resourceId))
    if (resource) booking.resource = resource
  }
  return booking
}

async function load({ reloadResources = false } = {}) {
  const sequence = ++loadSequence
  const period = {
    date_from: toDateInput(days.value[0]),
    date_to: toDateInput(days.value[days.value.length - 1]),
  }
  loading.value = true
  loadError.value = ''
  try {
    if (reloadResources || !resources.value.length) {
      const resourcePayload = await api.resources.list()
      if (sequence !== loadSequence) return
      resources.value = asList(resourcePayload, ['resources']).map(normalizeResource)
    }
    const [bookingPayload, blockPayload] = await Promise.all([
      api.bookings.listAll(period),
      api.ical.blocks(period),
    ])
    if (sequence !== loadSequence) return
    bookings.value = asList(bookingPayload, ['bookings'])
      .map(enrichBooking)
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
    calendarBlocks.value = asList(blockPayload, ['blocks'])
  } catch (error) {
    if (sequence === loadSequence) {
      loadError.value = error.message || 'Не удалось загрузить календарь.'
      bookings.value = []
      calendarBlocks.value = []
      show(loadError.value, 'error')
    }
  } finally {
    if (sequence === loadSequence) loading.value = false
  }
}

function movePeriod(amount) {
  focusedDate.value = viewMode.value === 'week'
    ? addDays(visibleStart.value, amount * 7)
    : addMonths(visibleStart.value, amount)
  load()
}

function goToday() {
  focusedDate.value = new Date()
  load()
}

function changeView(mode) {
  if (viewMode.value === mode) return
  viewMode.value = mode
  load()
}

function weekdayLabel(day) {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(day)
}

function openCreate(resource, day) {
  editingBooking.value = null
  initialResourceId.value = resource.id
  initialStartsAt.value = `${toDateInput(day)}T12:00`
  initialEndsAt.value = ''
  dialogOpen.value = true
}

function openEdit(booking) {
  editingBooking.value = booking
  initialResourceId.value = null
  initialStartsAt.value = ''
  initialEndsAt.value = ''
  dialogOpen.value = true
}

async function openAvailabilityBooking(candidate) {
  let resource = candidate.resource && String(candidate.resource.id) === String(candidate.resourceId)
    ? normalizeResource(candidate.resource)
    : resources.value.find((item) => String(item.id) === String(candidate.resourceId))
  if (!resource) {
    try {
      resource = normalizeResource(await api.resources.get(candidate.resourceId))
    } catch (error) {
      show(error.message || 'Не удалось загрузить выбранный объект.', 'error')
      return
    }
  }
  if (!resource.isActive) {
    show('Объект больше не активен. Обновите поиск доступности.', 'error')
    return
  }
  resources.value = resources.value.some((item) => String(item.id) === String(resource.id))
    ? resources.value.map((item) => String(item.id) === String(resource.id) ? resource : item)
    : [...resources.value, resource]
  editingBooking.value = null
  initialResourceId.value = resource.id
  initialStartsAt.value = candidate.startsAt
  initialEndsAt.value = candidate.endsAt
  dialogOpen.value = true
}

async function saveBooking(form) {
  saving.value = true
  try {
    if (editingBooking.value) {
      await api.bookings.update(editingBooking.value.id, serializeBooking(form))
      show('Бронирование обновлено.')
    } else {
      await api.bookings.create(serializeBooking(form))
      show('Бронирование создано.')
    }
    dialogOpen.value = false
    await load()
    await availabilityPanel.value?.refresh()
  } catch (error) {
    show(error.message || 'Не удалось сохранить бронирование.', 'error')
  } finally {
    saving.value = false
  }
}

onMounted(() => load({ reloadResources: true }))
</script>

<template>
  <div class="page calendar-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">План размещения</span>
        <h1>Календарь загрузки</h1>
        <p class="page-subtitle">
          {{ viewMode === 'week' ? 'Недельный вид занятости всех активных объектов.' : 'Месячный шахматный вид занятости всех активных объектов.' }}
        </p>
      </div>
      <button v-if="activeResources.length" type="button" class="button button--primary" @click="openCreate(activeResources[0], new Date())">
        <AppIcon name="plus" :size="18" />
        Новая бронь
      </button>
      <RouterLink v-else-if="canManage" to="/resources?new=1" class="button button--primary">
        <AppIcon name="plus" :size="18" />
        Добавить объект
      </RouterLink>
    </header>

    <AvailabilityPanel
      ref="availabilityPanel"
      :resources="resources"
      :initial-month="toDateInput(visibleStart).slice(0, 7)"
      @book="openAvailabilityBooking"
    />

    <section class="calendar-toolbar" aria-label="Управление календарём">
      <div class="week-controls">
        <button type="button" class="icon-button icon-button--bordered" :aria-label="previousPeriodLabel" @click="movePeriod(-1)">
          <AppIcon name="chevron-left" :size="18" />
        </button>
        <button type="button" class="button button--secondary button--compact" @click="goToday">Сегодня</button>
        <button type="button" class="icon-button icon-button--bordered" :aria-label="nextPeriodLabel" @click="movePeriod(1)">
          <AppIcon name="chevron-right" :size="18" />
        </button>
        <h2>{{ periodLabel }}</h2>
      </div>
      <div class="calendar-toolbar__filters">
        <div class="calendar-view-switch" role="group" aria-label="Вид календаря">
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'week' }"
            :aria-pressed="viewMode === 'week'"
            @click="changeView('week')"
          >
            Неделя
          </button>
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'month' }"
            :aria-pressed="viewMode === 'month'"
            @click="changeView('month')"
          >
            Месяц
          </button>
        </div>
        <label class="compact-field">
          <span class="sr-only">Тип объекта</span>
          <select v-model="typeFilter">
            <option value="">Все типы объектов</option>
            <option value="house">Дома</option>
            <option value="bathhouse">Бани</option>
            <option value="grill_house">Гриль-домики</option>
            <option value="banquet_hall">Банкетные залы</option>
          </select>
        </label>
      </div>
    </section>

    <LoadingState v-if="loading" />

    <section v-else-if="loadError" class="calendar-load-error" role="alert">
      <p class="form-error">{{ loadError }}</p>
      <button type="button" class="button button--secondary" @click="load({ reloadResources: true })">Повторить загрузку календаря</button>
    </section>

    <section v-else-if="filteredResources.length" class="calendar-board" :aria-label="`${viewMode === 'week' ? 'Недельная' : 'Месячная'} занятость объектов`">
      <div class="calendar-scroll">
        <div class="timeline-grid" :class="{ 'timeline-grid--month': viewMode === 'month' }" :style="{ '--day-count': days.length }">
          <div class="timeline-corner">
            <span>Объект</span>
            <small>{{ filteredResources.length }} активных</small>
          </div>
          <div
            v-for="day in days"
            :key="toDateInput(day)"
            class="timeline-day-header"
            :class="{ 'timeline-day-header--today': sameDay(day, new Date()), 'timeline-day-header--month': viewMode === 'month' }"
          >
            <span>{{ weekdayLabel(day) }}</span>
            <strong>{{ day.getDate() }}</strong>
            <small v-if="viewMode === 'week'">{{ new Intl.DateTimeFormat('ru-RU', { month: 'short' }).format(day) }}</small>
          </div>

          <template v-for="resource in filteredResources" :key="resource.id">
            <div class="timeline-resource">
              <span class="resource-accent" :style="{ backgroundColor: resource.color || 'var(--terracotta)' }" aria-hidden="true" />
              <div>
                <button type="button" class="timeline-resource-link" :aria-label="`Свободные даты: ${resource.name}`" title="Посмотреть свободные даты этого объекта" @click="availabilityPanel?.selectResource(resource.id)"><strong>{{ resource.name }}</strong></button>
                <small>{{ resourceTypeLabels[resource.resourceType] }} · {{ resource.capacity }} чел.</small>
              </div>
            </div>
            <div
              v-for="day in days"
              :key="`${resource.id}-${toDateInput(day)}`"
              class="timeline-cell"
              :class="{ 'timeline-cell--today': sameDay(day, new Date()) }"
            >
              <button
                v-for="booking in bookingsFor(resource.id, day)"
                :key="booking.id"
                type="button"
                class="timeline-booking"
                :class="`timeline-booking--${booking.status}`"
                :style="{ '--booking-color': resource.color || 'var(--terracotta)' }"
                :title="`${booking.guestName}: ${formatDate(booking.startsAt)} ${formatTime(booking.startsAt)} — ${formatDate(booking.endsAt)} ${formatTime(booking.endsAt)}`"
                @click="openEdit(booking)"
              >
                <strong>{{ booking.guestName }}</strong>
                <span v-if="viewMode === 'week'">{{ formatTime(booking.startsAt) }}–{{ formatTime(booking.endsAt) }}</span>
              </button>
              <div
                v-for="block in blocksFor(resource.id, day)"
                :key="`ical-${block.id}`"
                class="timeline-block"
                role="note"
                :title="`Занято в Авито: с ${blockDateLabel(block.start_date)}, выезд ${blockDateLabel(block.end_date)}`"
                :aria-label="`Занято в Авито: ${resource.name}, ${formatDate(day)}`"
              >
                <strong>Авито</strong>
                <span v-if="viewMode === 'week'">Занято</span>
              </div>
              <button
                v-if="!bookingsFor(resource.id, day).length && !blocksFor(resource.id, day).length"
                type="button"
                class="timeline-add"
                :aria-label="`Добавить бронь: ${resource.name}, ${formatDate(day)}`"
                @click="openCreate(resource, day)"
              >
                <AppIcon name="plus" :size="16" />
                <span>Свободно</span>
              </button>
            </div>
          </template>
        </div>
      </div>
      <footer class="calendar-legend">
        <span><i class="legend-dot legend-dot--confirmed" /> Подтверждена</span>
        <span><i class="legend-dot legend-dot--pending" /> Ожидает</span>
        <span><i class="legend-dot legend-dot--checked" /> Гости заехали</span>
        <span><i class="legend-dot legend-dot--avito" /> Авито iCal</span>
        <small>Нажмите на свободный день, чтобы создать бронь.</small>
      </footer>
    </section>

    <EmptyState v-else title="Нет активных объектов" :text="canManage ? 'Добавьте или активируйте объект, чтобы планировать загрузку.' : 'Обратитесь к администратору для добавления объектов.'">
      <RouterLink v-if="canManage" to="/resources?new=1" class="button button--primary">Добавить объект</RouterLink>
    </EmptyState>

    <BookingDialog
      :open="dialogOpen"
      :booking="editingBooking"
      :resources="resources"
      :saving="saving"
      :initial-resource-id="initialResourceId"
      :initial-starts-at="initialStartsAt"
      :initial-ends-at="initialEndsAt"
      @close="dialogOpen = false"
      @save="saveBooking"
    />
  </div>
</template>

<style scoped>
.timeline-resource-link { display: block; padding: 0; border: 0; background: none; color: inherit; text-align: left; cursor: pointer; font: inherit; }
.timeline-resource-link:hover strong { text-decoration: underline; text-underline-offset: 3px; }
.timeline-resource-link:focus-visible { outline: 2px solid var(--forest); outline-offset: 4px; border-radius: 3px; }
.calendar-load-error { padding: 22px; border: 1px solid var(--line); border-radius: var(--radius); background: var(--paper); }
</style>
